import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { ROUTES_CONSTANTS } from "../../../constants";
import { Button, AuthCenteredLayout, BackToSignIn } from "../../../components";
import { PiEyeLight, PiEyeSlash } from "react-icons/pi";
import {
  verifyResetTokenApi,
  resetPasswordApi,
  verifySetPasswordTokenApi,
  setPasswordApi,
} from "../../../DAL";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isSetPassword = pathname === ROUTES_CONSTANTS.SET_PASSWORD;
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [step, setStep] = useState("validating"); // "validating" | "form" | "token-error" | "success"
  const [tokenErrorKey, setTokenErrorKey] = useState("");
  const [viewPassword, setViewPassword] = useState(false);
  const [viewConfirmPassword, setViewConfirmPassword] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        password: yup
          .string()
          .min(8, t("validation.passwordMin8"))
          .required(t("validation.passwordRequired")),
        confirmPassword: yup
          .string()
          .oneOf([yup.ref("password")], t("validation.passwordsNoMatch"))
          .required(t("validation.confirmPasswordRequired")),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  // Validate token on mount
  const tokenMutation = useMutation({
    mutationFn: isSetPassword ? verifySetPasswordTokenApi : verifyResetTokenApi,
    onSuccess: () => setStep("form"),
    onError: (error) => {
      setTokenErrorKey(error.message);
      setStep("token-error");
    },
  });

  useEffect(() => {
    if (!token || !email) {
      setTokenErrorKey("apiErrors.noToken");
      setStep("token-error");
      return;
    }
    tokenMutation.mutate({ email, token });
  }, []);

  const resetMutation = useMutation({
    mutationFn: isSetPassword ? setPasswordApi : resetPasswordApi,
    onSuccess: () => setStep("success"),
  });

  const onSubmit = (data) => {
    resetMutation.mutate({
      email,
      token,
      password: data.password,
      password_confirmation: data.confirmPassword,
    });
  };

  // ─── Validating Token Screen ────────────────────────────────────────────────
  if (step === "validating") {
    return (
      <AuthCenteredLayout>
        <div className="text-gray-500 text-lg text-center">
          {t(isSetPassword ? "resetPassword.verifyingSet" : "resetPassword.verifying")}
        </div>
      </AuthCenteredLayout>
    );
  }

  // ─── Token Error Screen ─────────────────────────────────────────────────────
  if (step === "token-error") {
    return (
      <AuthCenteredLayout maxWidthClass="max-w-md">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 border-8 border-red-100 flex items-center justify-center mb-6">
            <span className="text-red-500 text-3xl font-bold">✕</span>
          </div>
          <div className="text-3xl sm:text-4xl font-medium text-black mb-3">
            {t("resetPassword.linkExpiredTitle")}
          </div>
          <div className="text-sm sm:text-base text-gray-500 mb-8">
            {t(tokenErrorKey)}
          </div>
          <Button
            size="lg"
            fullWidth
            onClick={() => navigate(ROUTES_CONSTANTS.FORGOT_PASSWORD)}
          >
            {t("resetPassword.btnRequestNewLink")}
          </Button>
          <BackToSignIn className="mt-8" />
        </div>
      </AuthCenteredLayout>
    );
  }

  // ─── Password Reset Confirmation Screen ─────────────────────────────────────
  if (step === "success") {
    return (
      <AuthCenteredLayout maxWidthClass="max-w-md">
        <div className="flex flex-col items-center text-center">
          <div className="text-3xl sm:text-4xl font-medium text-black mb-3">
            {t(isSetPassword ? "resetPassword.successTitleSet" : "resetPassword.successTitle")}
          </div>
          <div className="text-sm sm:text-base mb-8">
            {t(isSetPassword ? "resetPassword.successSubtitleSet" : "resetPassword.successSubtitle")}
          </div>

          <Button
            size="lg"
            fullWidth
            onClick={() => navigate(ROUTES_CONSTANTS.LOGIN)}
          >
            {t("resetPassword.btnSignIn")}
          </Button>
        </div>
      </AuthCenteredLayout>
    );
  }

  // ─── Change Password Form Screen ───────────────────────────────────────────
  return (
    <AuthCenteredLayout maxWidthClass="max-w-lg">
      <div className="text-3xl sm:text-4xl font-medium text-black mb-4 text-center">
        {t(isSetPassword ? "resetPassword.createPasswordTitle" : "resetPassword.changePasswordTitle")}
      </div>
      <div className="text-base sm:text-lg">
        {t("resetPassword.instructionPrefix")}{" "}
        <span className="font-semibold">
          {t("resetPassword.instructionBold")}
        </span>{" "}
        {t("resetPassword.instructionSuffix")}
        <ul className="mb-4 space-y-1 list-disc pl-5">
          <li>{t("resetPassword.reqLength")}</li>
          <li>{t("resetPassword.reqUsername")}</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* New Password */}
        <div className="mb-4">
          <label className="font-medium text-sm">
            {t("resetPassword.newPasswordLabel")}
          </label>
          <div className="relative">
            <input
              type={viewPassword ? "text" : "password"}
              placeholder="*****************************"
              {...register("password")}
              className="w-full border rounded-lg p-3 mt-2"
            />
            {viewPassword ? (
              <PiEyeSlash
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setViewPassword(false)}
              />
            ) : (
              <PiEyeLight
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setViewPassword(true)}
              />
            )}
          </div>
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Re-enter New Password */}
        <div className="mb-6">
          <label className="font-medium text-sm">
            {t("resetPassword.confirmPasswordLabel")}
          </label>
          <div className="relative">
            <input
              type={viewConfirmPassword ? "text" : "password"}
              placeholder="*****************************"
              {...register("confirmPassword")}
              className="w-full border rounded-lg p-3 mt-2"
            />
            {viewConfirmPassword ? (
              <PiEyeSlash
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setViewConfirmPassword(false)}
              />
            ) : (
              <PiEyeLight
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setViewConfirmPassword(true)}
              />
            )}
          </div>
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          loading={resetMutation.isPending}
          fullWidth
          size="lg"
        >
          {resetMutation.isPending
            ? t(isSetPassword ? "resetPassword.btnLoadingSet" : "resetPassword.btnLoading")
            : t(isSetPassword ? "resetPassword.btnSubmitSet" : "resetPassword.btnSubmit")}
        </Button>
      </form>

      <div className="mt-8 flex justify-center">
        <BackToSignIn />
      </div>
    </AuthCenteredLayout>
  );
};

export default ResetPassword;
